import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Loader2, Layers, Plus, Pencil, Trash2, GripVertical } from 'lucide-react'
import type { BracketTemplate } from './types'

interface BracketTemplatesSectionProps {
  templates: BracketTemplate[]
  templatesLoading: boolean
  // Form state
  showForm: boolean
  editingTemplate: BracketTemplate | null
  name: string
  onNameChange: (value: string) => void
  description: string
  onDescriptionChange: (value: string) => void
  // Actions
  onOpenForm: (template?: BracketTemplate) => void
  onCloseForm: () => void
  onSave: () => Promise<void>
  onDelete: (template: BracketTemplate) => void
  saving: boolean
  deletingId: string | null
  error: string | null
  // Drag and drop
  draggingTemplateId: string | null
  dragOverTemplateId: string | null
  reorderingSaving: boolean
  onDragStart: (e: React.DragEvent, templateId: string) => void
  onDragOver: (e: React.DragEvent, templateId: string) => void
  onDragLeave: () => void
  onDragEnd: () => void
  onDrop: (e: React.DragEvent, templateId: string) => void
}

export function BracketTemplatesSection({
  templates,
  templatesLoading,
  showForm,
  editingTemplate: _editingTemplate,
  name,
  onNameChange,
  description,
  onDescriptionChange,
  onOpenForm,
  onCloseForm,
  onSave,
  onDelete,
  saving,
  deletingId,
  error,
  draggingTemplateId,
  dragOverTemplateId,
  reorderingSaving,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDragEnd,
  onDrop,
}: BracketTemplatesSectionProps) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Layers className="h-4 w-4" />
            Bracket Types
          </CardTitle>
          <Button variant="ghost" size="sm" className="h-7" onClick={() => onOpenForm()}>
            <Plus className="h-3.5 w-3.5" />
          </Button>
        </div>
        <CardDescription className="text-xs">
          Skill levels to activate per season
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-0">
        {showForm && (
          <div className="mb-3 p-3 rounded-lg border bg-muted/30 space-y-2">
            <Input 
              placeholder="Name" 
              value={name} 
              onChange={e => onNameChange(e.target.value)} 
              className="h-8 text-sm" 
            />
            <Input 
              placeholder="Description (optional)" 
              value={description} 
              onChange={e => onDescriptionChange(e.target.value)} 
              className="h-8 text-sm" 
            />
            {error && <p className="text-xs text-destructive">{error}</p>}
            <div className="flex justify-end gap-1">
              <Button variant="ghost" size="sm" className="h-7" onClick={onCloseForm}>Cancel</Button>
              <Button size="sm" className="h-7" onClick={onSave} disabled={saving}>
                {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}
              </Button>
            </div>
          </div>
        )}

        {templatesLoading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : templates.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-sm text-muted-foreground mb-2">No bracket types yet</p>
            <Button variant="outline" size="sm" onClick={() => onOpenForm()}>
              <Plus className="h-3.5 w-3.5 mr-1" />
              Add First
            </Button>
          </div>
        ) : (
          <div className="space-y-1">
            {templates.map((template, i) => (
              <div
                key={template.templateId}
                draggable
                onDragStart={(e) => onDragStart(e, template.templateId)}
                onDragOver={(e) => onDragOver(e, template.templateId)}
                onDragLeave={onDragLeave}
                onDragEnd={onDragEnd}
                onDrop={(e) => onDrop(e, template.templateId)}
                className={`flex items-center gap-2 p-2 rounded-md hover:bg-muted/50 group cursor-grab active:cursor-grabbing transition-all ${
                  draggingTemplateId === template.templateId ? 'opacity-50' : ''
                } ${
                  dragOverTemplateId === template.templateId ? 'bg-primary/10 border-2 border-dashed border-primary' : ''
                }`}
              >
                <GripVertical className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="h-5 w-5 rounded bg-primary/10 flex items-center justify-center text-xs font-medium text-primary shrink-0">
                  {i + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium truncate block">{template.name}</span>
                </div>
                <div className="flex items-center gap-0.5 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                  <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onOpenForm(template)}>
                    <Pencil className="h-3 w-3" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-6 w-6" 
                    disabled={deletingId === template.templateId} 
                    onClick={() => onDelete(template)}
                  >
                    {deletingId === template.templateId ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Trash2 className="h-3 w-3" />
                    )}
                  </Button>
                </div>
              </div>
            ))}
            {reorderingSaving && (
              <div className="flex items-center justify-center gap-2 py-2 text-xs text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" />
                Saving order...
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
